import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Payment, PaymentIntent, PaymentIntentStatus, PaymentStatus } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CreatePaymentIntentForm } from "../components/CreatePaymentIntentForm.js";
import { FormError, TextInput, apiErrorMessage } from "../components/Form.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatDateTime, formatMoney } from "../lib/utils.js";

const PAYMENT_TONE: Record<PaymentStatus, BadgeTone> = {
  pending: "warning",
  settled: "success",
  failed: "danger",
  refunded: "neutral",
  voided: "danger",
};

/** Manual channels are the only voidable ones (mirrors the API guard). */
const VOIDABLE_CHANNELS = new Set([
  "manual_bank_transfer",
  "manual_cash",
  "manual_mobile_money_other",
  "manual_correction",
]);

const INTENT_TONE: Record<PaymentIntentStatus, BadgeTone> = {
  pending: "warning",
  matched: "success",
  expired: "neutral",
  cancelled: "danger",
};

export function PaymentsPage(): JSX.Element {
  const auth = useAuth();
  const qc = useQueryClient();
  const canVoid = (auth.user?.permissions ?? []).includes("payment.void");
  const [activeTab, setActiveTab] = useState<"payments" | "intents">("payments");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const payments = useQuery({
    queryKey: ["payments", "list"],
    queryFn: () => api.get<readonly Payment[]>("/api/payments"),
  });
  const intents = useQuery({
    queryKey: ["payment-intents", "list"],
    queryFn: () => api.get<readonly PaymentIntent[]>("/api/payment-intents"),
  });

  // Calculate dynamic metrics from live query cache
  const settledPayments = (payments.data ?? []).filter((p) => p.status === "settled");
  const totalRevenueMinor = settledPayments.reduce((sum, p) => sum + p.amountMinor, 0);
  const activeIntentsCount = (intents.data ?? []).filter((i) => i.status === "pending").length;

  // Filter handlers
  const filteredPayments = (payments.data ?? []).filter((p) => {
    const matchesSearch =
      search === "" ||
      p.providerReference.toLowerCase().includes(search.toLowerCase()) ||
      p.id.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    const matchesChannel = channelFilter === "all" || p.channel === channelFilter;
    return matchesSearch && matchesStatus && matchesChannel;
  });

  const filteredIntents = (intents.data ?? []).filter((i) => {
    const matchesSearch =
      search === "" ||
      i.reference.toLowerCase().includes(search.toLowerCase()) ||
      i.id.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || i.status === statusFilter;
    const matchesChannel = channelFilter === "all" || i.channel === channelFilter;
    return matchesSearch && matchesStatus && matchesChannel;
  });

  // Reset filters on tab change
  const handleTabChange = (tab: "payments" | "intents") => {
    setActiveTab(tab);
    setSearch("");
    setStatusFilter("all");
    setChannelFilter("all");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-xl font-bold text-simuflex-navy">Payments Hub</h1>
        <p className="text-sm text-simuflex-slate">
          Reconcile transactions, manage billing intents, and view provider webhook records.
        </p>
      </div>

      {/* Analytics Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="bg-gradient-to-br from-white to-simuflex-mist/20">
          <CardBody>
            <p className="text-[11px] font-bold text-simuflex-slate uppercase tracking-wider">
              Settled Revenue (TZS)
            </p>
            <p className="text-2xl font-black text-simuflex-navy mt-1">
              {formatMoney(totalRevenueMinor, "TZS")}
            </p>
            <p className="text-[10px] text-simuflex-forestGreen font-bold mt-1">
              ✓ Verified on-chain
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <p className="text-[11px] font-bold text-simuflex-slate uppercase tracking-wider">
              Success Transactions
            </p>
            <p className="text-2xl font-black text-simuflex-navy mt-1">{settledPayments.length}</p>
            <p className="text-[10px] text-simuflex-slate font-medium mt-1">
              Settlement rate 100% (mocked)
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <p className="text-[11px] font-bold text-simuflex-slate uppercase tracking-wider">
              Active Billing Intents
            </p>
            <p className="text-2xl font-black text-simuflex-navy mt-1">{activeIntentsCount}</p>
            <p className="text-[10px] text-simuflex-orange font-bold mt-1">
              ⏳ Awaiting M-Pesa push
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Tabs selector */}
      <div className="border-b border-simuflex-line flex justify-between items-center pb-px">
        <div className="bg-simuflex-mist/70 p-1 rounded-xl inline-flex space-x-1 border border-simuflex-line">
          <button
            type="button"
            onClick={() => handleTabChange("payments")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "payments"
                ? "bg-white text-simuflex-navy shadow-sm"
                : "text-simuflex-slate hover:text-simuflex-navy"
            }`}
          >
            Transactions Dashboard
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("intents")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "intents"
                ? "bg-white text-simuflex-navy shadow-sm"
                : "text-simuflex-slate hover:text-simuflex-navy"
            }`}
          >
            Payment Intents ({intents.data?.length ?? 0})
          </button>
        </div>
      </div>

      {/* Filters Area */}
      <Card className="bg-simuflex-mist/20 shadow-none border-simuflex-line/60">
        <CardBody className="py-3.5 px-5 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder={
                activeTab === "payments"
                  ? "Search by provider reference…"
                  : "Search by billing reference…"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-simuflex-line bg-white px-3.5 py-1.5 text-xs text-simuflex-navy placeholder:text-simuflex-slate/70 focus:border-simuflex-navy focus:outline-none focus:ring-1 focus:ring-simuflex-navy/10 transition-all duration-150"
            />
          </div>

          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-lg border border-simuflex-line bg-white px-3 py-1.5 text-xs text-simuflex-navy focus:border-simuflex-navy focus:outline-none transition-all duration-150"
            >
              <option value="all">All Statuses</option>
              {activeTab === "payments" ? (
                <>
                  <option value="pending">Pending</option>
                  <option value="settled">Settled</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </>
              ) : (
                <>
                  <option value="pending">Pending</option>
                  <option value="matched">Matched</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </>
              )}
            </select>
          </div>

          <div className="w-40">
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="w-full rounded-lg border border-simuflex-line bg-white px-3 py-1.5 text-xs text-simuflex-navy focus:border-simuflex-navy focus:outline-none transition-all duration-150"
            >
              <option value="all">All Channels</option>
              <option value="mpesa-tz">M-Pesa TZ</option>
              <option value="manual_cash">Manual Cash</option>
              <option value="manual_bank_transfer">Manual Bank</option>
              <option value="manual_correction">Manual Corr.</option>
            </select>
          </div>
        </CardBody>
      </Card>

      {/* Tab Workspaces */}
      {activeTab === "payments" ? (
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle>Transactions List</CardTitle>
            <span className="text-xs text-simuflex-slate font-semibold">
              {filteredPayments.length} transactions shown
            </span>
          </CardHeader>
          <CardBody className="p-0">
            {payments.isLoading ? (
              <p className="p-5 text-sm text-simuflex-slate">Loading payments…</p>
            ) : payments.isError ? (
              <p className="p-5 text-sm text-simuflex-orange font-bold">
                Failed to load payments ledger.
              </p>
            ) : filteredPayments.length === 0 ? (
              <p className="p-5 text-sm text-simuflex-slate">No payments match your filters.</p>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <Th>Provider Ref</Th>
                    <Th>Channel</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                    <Th>Settled Date</Th>
                    <Th>Created</Th>
                  </tr>
                </THead>
                <TBody>
                  {filteredPayments.map((p) => (
                    <PaymentRows
                      key={p.id}
                      payment={p}
                      expanded={expandedId === p.id}
                      onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                      canVoid={canVoid}
                      onVoided={() => {
                        void qc.invalidateQueries({ queryKey: ["payments", "list"] });
                        void qc.invalidateQueries({ queryKey: ["payments"] });
                      }}
                    />
                  ))}
                </TBody>
              </Table>
            )}
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6 animate-fade-in">
          {/* Create intent inline widget */}
          <CreatePaymentIntentForm />

          {/* Intents Table */}
          <Card>
            <CardHeader>
              <CardTitle>Generated Intents</CardTitle>
              <span className="text-xs text-simuflex-slate font-semibold">
                {filteredIntents.length} intents shown
              </span>
            </CardHeader>
            <CardBody className="p-0">
              {intents.isLoading ? (
                <p className="p-5 text-sm text-simuflex-slate">Loading intents…</p>
              ) : intents.isError ? (
                <p className="p-5 text-sm text-simuflex-orange font-bold">
                  Failed to load intents directory.
                </p>
              ) : filteredIntents.length === 0 ? (
                <p className="p-5 text-sm text-simuflex-slate">No intents match your filters.</p>
              ) : (
                <Table>
                  <THead>
                    <tr>
                      <Th>Billing Reference</Th>
                      <Th>Customer</Th>
                      <Th>Channel / Method</Th>
                      <Th>Amount</Th>
                      <Th>Status</Th>
                      <Th>Expires At</Th>
                      <Th>Created</Th>
                    </tr>
                  </THead>
                  <TBody>
                    {filteredIntents.map((i) => (
                      <Tr key={i.id}>
                        <Td className="font-mono text-xs text-simuflex-navy/80">{i.reference}</Td>
                        <Td className="text-xs">
                          {i.customerId ? (
                            <Link
                              to="/customers/$id"
                              params={{ id: i.customerId }}
                              className="font-semibold text-simuflex-navy hover:underline"
                            >
                              {i.customerName ?? "Customer"}
                            </Link>
                          ) : i.customerName ? (
                            <span className="font-semibold text-simuflex-navy">
                              {i.customerName}
                            </span>
                          ) : (
                            <span className="text-simuflex-slate">—</span>
                          )}
                          {i.customerPhone ? (
                            <div className="font-mono text-[11px] text-simuflex-slate">
                              {i.customerPhone}
                              {i.payerPhoneKnown === false ? (
                                <span className="ml-1 font-sans not-italic text-simuflex-slate/70">
                                  (on file)
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                          <Link
                            to="/loans/$id"
                            params={{ id: i.loanId }}
                            className="text-[10px] text-simuflex-slate hover:underline"
                          >
                            view loan →
                          </Link>
                        </Td>
                        <Td className="text-xs font-semibold">
                          {i.channel}
                          {i.method ? (
                            <div className="text-[11px] font-normal text-simuflex-slate">
                              {i.method}
                            </div>
                          ) : null}
                        </Td>
                        <Td className="font-bold">{formatMoney(i.amountMinor, i.currency)}</Td>
                        <Td>
                          <Badge tone={INTENT_TONE[i.status]}>
                            {i.lastError ? "failed" : i.status}
                          </Badge>
                          {i.lastError ? (
                            <p className="mt-1 max-w-xs text-[11px] leading-snug text-simuflex-orange">
                              {i.lastError}
                            </p>
                          ) : null}
                        </Td>
                        <Td className="text-xs text-simuflex-slate font-medium">
                          {formatDateTime(i.expiresAt)}
                        </Td>
                        <Td className="text-xs text-simuflex-slate font-medium">
                          {formatDateTime(i.createdAt)}
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

/** Small key/value row for the expanded detail grid. */
function Detail({ k, v }: { readonly k: string; readonly v: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-simuflex-slate">{k}</dt>
      <dd className="font-mono text-xs text-simuflex-navy break-all">{v}</dd>
    </div>
  );
}

/**
 * A payment's summary row plus its expandable detail row. The detail panel shows
 * the full record and — for a settled manual payment — a void action (reason
 * required), gated on `payment.void`.
 */
function PaymentRows({
  payment: p,
  expanded,
  onToggle,
  canVoid,
  onVoided,
}: {
  readonly payment: Payment;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly canVoid: boolean;
  readonly onVoided: () => void;
}): JSX.Element {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const voidable = canVoid && p.status === "settled" && VOIDABLE_CHANNELS.has(p.channel);

  const voidMut = useMutation({
    mutationFn: () => api.post<Payment>(`/api/payments/${p.id}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      setError(null);
      setReason("");
      onVoided();
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  return (
    <>
      <Tr
        onClick={onToggle}
        className={expanded ? "cursor-pointer bg-simuflex-navy/5" : "cursor-pointer"}
      >
        <Td className="font-mono text-xs text-simuflex-navy/80">{p.providerReference}</Td>
        <Td className="text-xs font-semibold">{p.channel}</Td>
        <Td className="font-bold">{formatMoney(p.amountMinor, p.currency)}</Td>
        <Td>
          <Badge tone={PAYMENT_TONE[p.status]}>{p.status}</Badge>
        </Td>
        <Td className="text-xs text-simuflex-slate font-medium">
          {p.settledAt ? formatDateTime(p.settledAt) : "—"}
        </Td>
        <Td className="text-xs text-simuflex-slate font-medium">{formatDateTime(p.createdAt)}</Td>
      </Tr>
      {expanded ? (
        <Tr>
          <Td colSpan={6} className="bg-simuflex-mist/30">
            <div className="space-y-4 px-2 py-3">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
                <Detail k="Payment ID" v={p.id} />
                <Detail k="Loan ID" v={p.loanId} />
                <Detail k="Intent ID" v={p.intentId ?? "—"} />
                <Detail k="Channel" v={p.channel} />
                <Detail k="Amount" v={formatMoney(p.amountMinor, p.currency)} />
                <Detail k="Status" v={p.status} />
                <Detail k="Provider reference" v={p.providerReference} />
                <Detail k="Settled at" v={p.settledAt ? formatDateTime(p.settledAt) : "—"} />
                <Detail k="Created" v={formatDateTime(p.createdAt)} />
                <Detail k="Note / comment" v={p.note ?? "—"} />
                {p.status === "voided" ? (
                  <>
                    <Detail k="Void reason" v={p.voidReason ?? "—"} />
                    <Detail k="Voided at" v={p.voidedAt ? formatDateTime(p.voidedAt) : "—"} />
                  </>
                ) : null}
              </dl>

              {voidable ? (
                <div className="rounded-lg border border-simuflex-orange/30 bg-simuflex-orange/5 p-3">
                  <p className="mb-2 text-xs font-bold text-simuflex-orange">
                    Void this manual payment
                  </p>
                  <p className="mb-2 text-[11px] text-simuflex-slate">
                    Reverses its ledger entries and re-evaluates the loan&apos;s arrears. The reason
                    is recorded on the audit log.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <TextInput
                      value={reason}
                      placeholder="Reason (required)"
                      className="mt-0 max-w-xs"
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={reason.trim().length < 3 || voidMut.isPending}
                      onClick={() => voidMut.mutate()}
                    >
                      {voidMut.isPending ? "Voiding…" : "Void payment"}
                    </Button>
                  </div>
                  <div className="mt-2">
                    <FormError message={error} />
                  </div>
                </div>
              ) : p.status === "settled" && !canVoid ? (
                <p className="text-[11px] text-simuflex-slate">
                  Voiding requires the <code>payment.void</code> permission.
                </p>
              ) : p.status === "settled" ? (
                <p className="text-[11px] text-simuflex-slate">
                  Only manual payments can be voided; provider settlements are immutable.
                </p>
              ) : null}
            </div>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}
